import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { auditAPI, exportAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import {
  Shield,
  Download,
  Search,
  LogIn,
  Plus,
  Edit2,
  Trash2,
  FileSpreadsheet,
  Filter,
  User,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const ACTION_ICONS = {
  login: LogIn,
  create: Plus,
  update: Edit2,
  delete: Trash2,
  export: FileSpreadsheet,
};

const ACTION_COLORS = {
  login: 'bg-blue-100 text-blue-700',
  create: 'bg-green-100 text-green-700',
  update: 'bg-amber-100 text-amber-700',
  delete: 'bg-red-100 text-red-700',
  export: 'bg-purple-100 text-purple-700',
};

export const AuditTrailPage = () => {
  const [logs, setLogs] = useState([]);
  usePageTitle('Audit Trail');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) loadLogs();
  }, [actionFilter, resourceFilter]);

  const loadData = async () => {
    try {
      const [logsRes, statsRes] = await Promise.all([
        auditAPI.getAll({ limit: 200 }),
        auditAPI.getStats(),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const params = { limit: 200 };
      if (actionFilter !== 'all') params.action = actionFilter;
      if (resourceFilter !== 'all') params.resource = resourceFilter;
      const res = await auditAPI.getAll(params);
      setLogs(res.data);
    } catch (error) {
      toast.error('Failed to load logs');
    }
  };

  const handleExport = async () => {
    try {
      const res = await exportAPI.auditTrail();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'audit_trail_export.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Audit trail exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const filteredLogs = logs.filter((log) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      log.user_email?.toLowerCase().includes(term) ||
      log.details?.toLowerCase().includes(term) ||
      log.resource?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="audit-trail-page">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-gray-700 via-gray-600 to-gray-500 p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">Audit Trail</h1>
            <p className="text-gray-200">Complete log of all system activities</p>
          </div>
          <Button variant="outline" onClick={handleExport} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="export-audit-btn">
            <Download className="w-4 h-4 mr-2" /> Export Excel
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <Card className="p-3 text-center border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50">
          <p className="text-2xl font-bold text-blue-700">{stats?.total || 0}</p>
          <p className="text-xs text-blue-500 font-medium">Total</p>
        </Card>
        {stats?.by_action && Object.entries(stats.by_action).map(([action, count]) => {
          const Icon = ACTION_ICONS[action] || Shield;
          const colors = {create: 'from-emerald-50 to-teal-50 text-emerald-700', update: 'from-amber-50 to-orange-50 text-amber-700', delete: 'from-red-50 to-rose-50 text-red-700', login: 'from-sky-50 to-cyan-50 text-sky-700', verify: 'from-violet-50 to-purple-50 text-violet-700'};
          const c = colors[action] || 'from-slate-50 to-gray-50 text-slate-700';
          return (
            <Card key={action} className={`p-3 text-center border-0 shadow-md bg-gradient-to-br ${c.split(' ').slice(0,2).join(' ')}`}>
              <div className="flex items-center justify-center gap-1">
                <Icon className="w-3 h-3" />
                <p className={`text-2xl font-bold ${c.split(' ').slice(2).join(' ')}`}>{count}</p>
              </div>
              <p className="text-xs font-medium capitalize opacity-70">{action}s</p>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by user, details..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="audit-search"
          />
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-40" data-testid="audit-action-filter">
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="login">Login</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="export">Export</SelectItem>
          </SelectContent>
        </Select>
        <Select value={resourceFilter} onValueChange={setResourceFilter}>
          <SelectTrigger className="w-40" data-testid="audit-resource-filter">
            <SelectValue placeholder="Resource" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Resources</SelectItem>
            <SelectItem value="auth">Auth</SelectItem>
            <SelectItem value="clients">Clients</SelectItem>
            <SelectItem value="leads">Leads</SelectItem>
            <SelectItem value="phases">Phases</SelectItem>
            <SelectItem value="tasks">Tasks</SelectItem>
            <SelectItem value="invoices">Invoices</SelectItem>
            <SelectItem value="locations">Locations</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Logs Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => {
                  const Icon = ACTION_ICONS[log.action] || Shield;
                  return (
                    <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-3 h-3 text-muted-foreground" />
                          <div>
                            <p className="text-sm">{log.user_email || 'System'}</p>
                            <p className="text-xs text-muted-foreground capitalize">{log.user_role}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={ACTION_COLORS[log.action] || 'bg-slate-100'}>
                          <Icon className="w-3 h-3 mr-1" />
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{log.resource}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[300px]">
                        <p className="text-sm truncate">{log.details || '-'}</p>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {log.ip_address || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default AuditTrailPage;
